import React from 'react';
import { Dialog } from 'radix-ui';
import { XMarkIcon } from '@heroicons/react/20/solid';

import { classnames } from '../../utils';
import styles from './Drawer.module.scss';

/**
 * Drawer slide-over modal.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the drawer is open.
 * @param {(open: boolean) => void} props.onOpenChange - Callback when the drawer is opened or closed.
 * @param {React.ReactNode} props.children - Drawer content.
 * @param {string} [props.className] - Additional class names.
 * @returns {React.ReactElement}
 */
const Drawer = ({ open, onOpenChange, children, className = '', ...props }) => {
	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className={styles.overlay} />
				<Dialog.Content className={classnames(styles.drawer, className)} {...props}>
					{children}
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

/**
 * Drawer close button.
 *
 * @returns {React.ReactElement}
 */
const Close = () => {
	return (
		<Dialog.Close className={styles.close}>
			<XMarkIcon />
		</Dialog.Close>
	);
};

/**
 * Drawer title.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Title content.
 * @param {string} [props.className] - Additional class names.
 * @returns {React.ReactElement}
 */
const Title = ({ children, className = '' }) => {
	return <Dialog.Title className={classnames(styles.title, className)}>{children}</Dialog.Title>;
};

/**
 * Drawer header container.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Header content.
 * @param {string} [props.className] - Additional class names.
 * @returns {React.ReactElement}
 */
const Header = ({ children, className = '' }) => {
	return <div className={classnames(styles.header, className)}>{children}</div>;
};

Drawer.Close = Close;
Drawer.Title = Title;
Drawer.Header = Header;

export default Drawer;
